import { useEffect, useRef, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, Images, Play, Download, Link2 } from "lucide-react";
import { toast } from "sonner";
import { slugify, buildShareUrl, copyToClipboard, getAddressBarShareUrl } from "@/lib/share";

const VIDEO_EXTS = [".mp4", ".webm", ".mov", ".ogv", ".m4v"];
function isVideoUrl(url) {
  if (!url) return false;
  const clean = url.split("?")[0].split("#")[0].toLowerCase();
  return VIDEO_EXTS.some((ext) => clean.endsWith(ext));
}
function Media({ url, alt = "", mode = "thumb", className = "" }) {
  if (!url) return null;
  if (isVideoUrl(url)) {
    return (
      <video
        src={url}
        autoPlay
        muted
        loop
        playsInline
        controls={mode === "lightbox"}
        className={className}
      />
    );
  }
  return <img src={url} alt={alt} className={className} loading="lazy" />;
}

export default function PortfolioSection({ portfolio = [] }) {
  const [openCat, setOpenCat] = useState(null); // category object
  const [lightboxIdx, setLightboxIdx] = useState(null); // null = grid view; number = image idx

  // Reset lightbox when changing categories
  useEffect(() => {
    setLightboxIdx(null);
  }, [openCat]);

  // Lock body scroll while modal open
  useEffect(() => {
    if (openCat) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [openCat]);

  // Sync URL <-> open item. When a portfolio entry is opened, mirror the URL
  // bar to the SHARE URL (`/api/share/<slug>`) and push a real history entry
  // so the browser BACK button works. Closing via X pops the entry.
  const hadOpenCat = useRef(false);
  useEffect(() => {
    if (openCat) {
      const slug = slugify(openCat.name || openCat.title || openCat.id);
      if (!slug) return;
      const target = getAddressBarShareUrl(slug, "portfolio");
      if (window.location.pathname + window.location.search === target) {
        hadOpenCat.current = true;
        return;
      }
      if (hadOpenCat.current) {
        window.history.replaceState({ nmModal: slug }, "", target);
      } else {
        window.history.pushState({ nmModal: slug }, "", target);
        hadOpenCat.current = true;
      }
    } else if (hadOpenCat.current) {
      hadOpenCat.current = false;
      if (window.location.pathname.startsWith("/api/share/")) {
        window.history.back();
      }
    }
  }, [openCat]);

  // Listen for browser back/forward — close the modal cleanly when navigating off.
  useEffect(() => {
    const handler = () => {
      if (!window.location.pathname.startsWith("/api/share/")) {
        hadOpenCat.current = false;
        setOpenCat(null);
      }
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  // On mount: auto-open the matching portfolio entry from ?v=<slug>, if any.
  // Wait until the async portfolio prop is populated, then run exactly once.
  // Coordination with ProductsSection:
  //   - If hash is `#products`, the user wants the shop modal — skip entirely.
  //   - If ProductsSection already claimed the slug (window.__nullDeepLinkClaimed
  //     set synchronously in its effect, which runs first since it mounts first),
  //     skip — don't double-open.
  const deeplinkConsumed = useRef(false);
  useEffect(() => {
    if (deeplinkConsumed.current) return;
    if (!portfolio?.length) return;
    const params = new URLSearchParams(window.location.search);
    const slug = params.get("v");
    if (!slug) {
      deeplinkConsumed.current = true;
      return;
    }
    if (window.location.hash === "#products") return;
    if (window.__nullDeepLinkClaimed) {
      deeplinkConsumed.current = true;
      return;
    }
    const match = portfolio.find(
      (p) => slugify(p.name || p.title || p.id) === slug
    );
    if (match) {
      deeplinkConsumed.current = true;
      window.__nullDeepLinkClaimed = true;
      // Set up history so browser back returns to "/" (landing page) instead
      // of leaving the site. Rewrite the redirect entry to "/" then push the
      // share URL on top.
        try {
          const target = getAddressBarShareUrl(slug, "portfolio");
          // Preserve the current section page (e.g. "/portfolio" or "/") as
          // the previous history entry so back-button and modal-close return
          // the user to the right list, not always to homepage.
          const backTarget = window.location.pathname + window.location.search;
          window.history.replaceState({}, "", backTarget.startsWith("/api/share/") ? "/portfolio" : backTarget.split("?")[0]);
          window.history.pushState({ nmModal: slug }, "", target);
          hadOpenCat.current = true;
        } catch (_) { /* ignore */ }
      setOpenCat(match);
      setTimeout(() => {
        document.getElementById("portfolio")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  }, [portfolio]);

  const handleCopyShareLink = useCallback(async () => {
    if (!openCat) return;
    const slug = slugify(openCat.name || openCat.title || openCat.id);
    const url = buildShareUrl(slug, "portfolio");
    const ok = await copyToClipboard(url);
    if (ok) {
      toast.success("Share link copied", {
        description: "Pasting this in Discord/Twitter previews with the asset's thumbnail.",
      });
    } else {
      toast.error("Couldn't copy — try selecting the URL manually");
    }
  }, [openCat]);

  // Keyboard navigation
  const handleKey = useCallback((e) => {
    if (!openCat) return;
    if (e.key === "Escape") {
      if (lightboxIdx !== null) setLightboxIdx(null);
      else setOpenCat(null);
      return;
    }
    if (lightboxIdx !== null && openCat.images?.length) {
      if (e.key === "ArrowRight") {
        setLightboxIdx((i) => (i + 1) % openCat.images.length);
      } else if (e.key === "ArrowLeft") {
        setLightboxIdx((i) => (i - 1 + openCat.images.length) % openCat.images.length);
      }
    }
  }, [openCat, lightboxIdx]);

  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  if (!portfolio || portfolio.length === 0) return null;

  const sorted = [...portfolio].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <>
      <section
        id="portfolio"
        data-testid="portfolio-section"
        className="relative py-24 md:py-32 px-6 md:px-10 max-w-[100rem] mx-auto"
      >
        <div className="mb-16 md:mb-20 grid md:grid-cols-[1fr_auto] gap-8 items-end">
          <div>
            <div className="bracket mb-4 flex items-center gap-3">
              <span className="num-solid text-[10px]">/03</span>
              ARCHIVE
            </div>
            <h2
              className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold uppercase tracking-[-0.03em] leading-[0.9]"
            >
              PREVIOUS<br />
              <span className="text-[var(--accent)] text-glow">BUILDS.</span>
            </h2>
            <p className="mt-6 text-[var(--muted)] max-w-xl text-base md:text-lg leading-relaxed">
              Selected work from past commissions. Click any build to view the full image set.
            </p>
          </div>
          <div className="hidden md:block num-solid text-[140px] lg:text-[180px] opacity-20">
            ×{sorted.length}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {sorted.map((cat, i) => (
            <button
              key={cat.id}
              data-testid={`portfolio-card-${cat.id}`}
              onClick={() => setOpenCat(cat)}
              className="hover-rise nm-fade-up group relative aspect-[4/3] bg-[var(--theme-surface)]/60 border border-[var(--theme-border)] rounded-2xl overflow-hidden text-left"
              style={{ animationDelay: `${Math.min(i, 8) * 70}ms` }}
            >
              {cat.thumbnail ? (
                <>
                  <Media
                    url={cat.thumbnail}
                    alt={cat.name}
                    mode="thumb"
                    className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500"
                  />
                  {isVideoUrl(cat.thumbnail) && (
                    <span className="absolute top-3 right-3 z-10 bg-black/80 border border-[var(--accent)]/60 text-[var(--accent)] font-mono text-[9px] tracking-widest px-2 py-1 flex items-center gap-1">
                      <Play size={9} fill="currentColor" /> VIDEO
                    </span>
                  )}
                </>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-[var(--muted)] font-mono text-xs">
                  // no thumbnail
                </div>
              )}

              {/* Gradient + content overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-6">
                <div className="font-mono text-[10px] text-[var(--accent)] tracking-[0.3em] uppercase mb-2 flex items-center gap-2">
                  <span>/{String(i + 1).padStart(2, "0")}</span>
                  <span className="flex items-center gap-1">
                    <Images size={10} /> {cat.images?.length || 0} IMG
                  </span>
                </div>
                <div className="font-display text-2xl md:text-3xl font-extrabold uppercase tracking-[-0.02em] leading-tight">
                  {cat.name}
                </div>
                {cat.description && (
                  <p className="text-xs md:text-sm text-[var(--muted)] mt-2 line-clamp-2">
                    {cat.description}
                  </p>
                )}
                <div className="mt-3 font-mono text-[10px] text-[var(--accent)] tracking-widest uppercase opacity-0 group-hover:opacity-100 transition">
                  → view archive
                </div>
              </div>

              {/* Hazard corner */}
              <div className="absolute top-0 right-0 w-12 h-1 hazard" />

              {/* NOT FOR SALE — privately commissioned, never for purchase */}
              {cat.not_for_sale && (
                <div className="absolute top-3 left-3 z-10" data-testid={`portfolio-nfs-${cat.id}`}>
                  <span className="nfs-badge">NOT FOR SALE</span>
                </div>
              )}

              {/* GIVEAWAY WINNER — this build was awarded as a raffle prize.
                  Takes priority over DOWNLOAD NOW so the gold badge shows even
                  when the entry also has an external_url. */}
              {cat.giveaway_winner && !cat.not_for_sale && (
                <div className="absolute top-3 left-3 z-10" data-testid={`portfolio-gw-${cat.id}`}>
                  <span className="gw-badge">
                    GIVEAWAY WINNER{cat.giveaway_winner_name ? ` · ${cat.giveaway_winner_name}` : ""}
                  </span>
                </div>
              )}

              {/* DOWNLOAD NOW — free, available on an external host (GTA5-Mods etc.) */}
              {cat.external_url && !cat.not_for_sale && !cat.giveaway_winner && (
                <div className="absolute top-3 left-3 z-10" data-testid={`portfolio-dl-${cat.id}`}>
                  <span className="dl-badge">DOWNLOAD NOW!</span>
                </div>
              )}
            </button>
          ))}
        </div>
      </section>

      {/* MODAL — gallery grid. Modal starts BELOW the navbar (top-16) so that
          the internal `sticky top-0` header naturally sits flush against the
          navbar as the user scrolls through the images. */}
      {openCat && lightboxIdx === null && (
        <div
          data-testid="portfolio-modal"
          className="fixed left-0 right-0 top-16 bottom-0 z-[100] bg-black/95 backdrop-blur-md overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget) setOpenCat(null); }}
        >
          <div className="min-h-full p-6 md:p-12 max-w-[100rem] mx-auto">
            {/* Header (stays at top of modal viewport while user scrolls images) */}
            <div className="flex items-start justify-between gap-6 pb-6 mb-8 border-b border-[var(--accent-dim)] sticky top-0 bg-black/90 backdrop-blur-md z-10 pt-2">
              <div>
                <div className="bracket mb-2 flex items-center gap-3 flex-wrap">
                  <span>// ARCHIVE_ENTRY // {openCat.images?.length || 0} IMAGES</span>
                  {openCat.not_for_sale && (
                    <span className="nfs-badge nfs-badge-lg" data-testid="portfolio-modal-nfs">
                      NOT FOR SALE — PRIVATE COMMISSION
                    </span>
                  )}
                  {openCat.giveaway_winner && !openCat.not_for_sale && (
                    <span className="gw-badge gw-badge-lg" data-testid="portfolio-modal-gw">
                      GIVEAWAY WINNER
                      {openCat.giveaway_winner_name ? ` · ${openCat.giveaway_winner_name.toUpperCase()}` : ""}
                    </span>
                  )}
                  {openCat.external_url && !openCat.not_for_sale && !openCat.giveaway_winner && (
                    <span className="dl-badge dl-badge-lg" data-testid="portfolio-modal-dl">
                      DOWNLOAD NOW — FREE
                    </span>
                  )}
                </div>
                <h3
                  data-text={openCat.name}
                  className="glitch font-display text-3xl md:text-5xl font-extrabold uppercase tracking-[-0.02em]"
                >
                  {openCat.name}
                </h3>
                {openCat.description && (
                  <p className="mt-3 text-[var(--muted)] max-w-2xl text-sm md:text-base">
                    {openCat.description}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  data-testid="portfolio-share"
                  type="button"
                  onClick={handleCopyShareLink}
                  title="Copy share link — Discord/Twitter will preview with this asset's thumbnail"
                  className="group inline-flex items-center gap-2 h-11 px-3 md:px-4 rounded-full border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-black transition font-mono text-[10px] md:text-xs uppercase tracking-widest"
                >
                  <Link2 size={14} />
                  <span className="hidden md:inline">Share</span>
                </button>
                {openCat.external_url && (
                  <a
                    data-testid="portfolio-external-link"
                    href={openCat.external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Download / view on external site"
                    className="group inline-flex items-center gap-2 h-11 px-3 md:px-4 rounded-full border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-black transition font-mono text-[10px] md:text-xs uppercase tracking-widest"
                  >
                    <Download size={14} />
                    <span className="hidden md:inline">Download</span>
                  </a>
                )}
                <button
                  data-testid="portfolio-close"
                  onClick={() => setOpenCat(null)}
                  className="flex-shrink-0 w-11 h-11 flex items-center justify-center rounded-full border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-black transition"
                  aria-label="close"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Gallery grid */}
            {openCat.images?.length ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {openCat.images.map((img, idx) => (
                  <button
                    key={img.id}
                    data-testid={`portfolio-image-${img.id}`}
                    onClick={() => setLightboxIdx(idx)}
                    className="group relative aspect-[4/3] bg-[var(--theme-surface)]/60 border border-[var(--theme-border)] rounded-xl overflow-hidden hover:border-[var(--accent)] transition"
                  >
                    <Media
                      url={img.url}
                      alt={img.caption || openCat.name}
                      mode="thumb"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    {isVideoUrl(img.url) && (
                      <span className="absolute bottom-2 left-2 z-10 bg-black/80 border border-[var(--accent)]/60 text-[var(--accent)] font-mono text-[9px] tracking-widest px-1.5 py-0.5 flex items-center gap-1">
                        <Play size={8} fill="currentColor" /> VIDEO
                      </span>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition" />
                    {img.caption && (
                      <div className="absolute bottom-0 left-0 right-0 p-3 font-mono text-[10px] text-white tracking-widest uppercase opacity-0 group-hover:opacity-100 transition">
                        {img.caption}
                      </div>
                    )}
                    <div className="absolute top-2 right-2 font-mono text-[9px] text-[var(--accent)] tracking-widest">
                      {String(idx + 1).padStart(2, "0")}/{String(openCat.images.length).padStart(2, "0")}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="py-20 text-center font-mono text-sm text-[var(--muted)]">
                // no images yet in this archive
              </div>
            )}
          </div>
        </div>
      )}

      {/* LIGHTBOX — single image */}
      {openCat && lightboxIdx !== null && openCat.images?.[lightboxIdx] && (
        <div
          data-testid="portfolio-lightbox"
          className="fixed inset-0 z-[110] bg-black/98 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setLightboxIdx(null); }}
        >
          <button
            data-testid="lightbox-close"
            onClick={() => setLightboxIdx(null)}
            className="absolute top-5 right-5 w-11 h-11 flex items-center justify-center rounded-full border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-black transition z-10"
            aria-label="close"
          >
            <X size={18} />
          </button>

          {openCat.images.length > 1 && (
            <>
              <button
                onClick={() => setLightboxIdx((i) => (i - 1 + openCat.images.length) % openCat.images.length)}
                className="absolute left-5 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-black transition z-10"
                aria-label="previous"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={() => setLightboxIdx((i) => (i + 1) % openCat.images.length)}
                className="absolute right-5 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-black transition z-10"
                aria-label="next"
              >
                <ChevronRight size={20} />
              </button>
            </>
          )}

          <div className="max-w-[90vw] max-h-[88vh] flex flex-col items-center gap-4">
            <Media
              url={openCat.images[lightboxIdx].url}
              alt={openCat.images[lightboxIdx].caption || openCat.name}
              mode="lightbox"
              className="max-w-full max-h-[80vh] object-contain border border-[var(--accent-dim)]"
            />
            <div className="font-mono text-[10px] text-[var(--muted)] tracking-widest uppercase text-center">
              {openCat.images[lightboxIdx].caption ? (
                <span className="text-white">{openCat.images[lightboxIdx].caption}</span>
              ) : openCat.name}
              <span className="text-[var(--accent)] ml-3">
                // {String(lightboxIdx + 1).padStart(2, "0")} / {String(openCat.images.length).padStart(2, "0")}
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
